import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type InputHTMLAttributes,
} from 'react';
import { IconArrowsExchange, IconTrash, IconUpload, IconZoomIn } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { Popover } from './Popover.js';
import { WP_IMAGE_FILE_ACCEPT } from './FilePickerField.js';
import {
  GalleryImagePickerPanel,
  type GalleryImagePickerItem,
} from './GalleryImagePickerPanel.js';
import { assignFilesToInput } from '../utils/clipboardImage.js';
import { useClipboardImagePaste } from '../utils/useClipboardImagePaste.js';
import {
  ImageLightbox,
  imageLightboxActionClass,
  imageLightboxActionDangerClass,
} from './ImageLightbox.js';
import styles from './ThumbnailImagePicker.module.css';
import { Pressable } from './Pressable.js';

export type { GalleryImagePickerItem };

export type ThumbnailImagePickerProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children' | 'className' | 'size'
> & {
  className?: string;
  /** Image URL to show in the tile (blob or remote). */
  previewUrl?: string | null;
  /** Compact control, large square stage, or full-width cover banner. */
  size?: 'sm' | 'lg' | 'banner';
  chooseLabel?: string;
  changeLabel?: string;
  removeLabel?: string;
  previewLabel?: string;
  onRemove?: () => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** Gallery-first: existing images to pick before upload. */
  galleryItems?: GalleryImagePickerItem[];
  /** Selected gallery item id (key). */
  selectedGalleryId?: string | null;
  onGallerySelect?: (id: string) => void;
  /** Called when user uploads via gallery panel (in addition to onChange). */
  onGalleryUpload?: (file: File) => void;
  uploadLabel?: string;
  emptyGalleryLabel?: string;
};

export function ThumbnailImagePicker({
  previewUrl = null,
  size = 'sm',
  chooseLabel = 'Choisir une image',
  changeLabel = "Changer l'image",
  removeLabel = "Retirer l'image",
  previewLabel = "Agrandir l'image",
  onRemove,
  onChange,
  galleryItems = [],
  selectedGalleryId = null,
  onGallerySelect,
  onGalleryUpload,
  uploadLabel = 'Téléverser une nouvelle image',
  emptyGalleryLabel,
  disabled,
  id,
  accept = WP_IMAGE_FILE_ACCEPT,
  multiple,
  name,
  className,
  ...rest
}: ThumbnailImagePickerProps) {
  const autoId = useId();
  const inputId = id ?? `wp-thumb-img-${autoId}`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const large = size === 'lg';
  const banner = size === 'banner';
  const tileClass = cx(styles.tile, large && styles.tileLg, banner && styles.tileBanner);
  const emptyTileClass = cx(
    styles.emptyTile,
    large && styles.emptyTileLg,
    banner && styles.emptyTileBanner,
  );
  const thumbClass = cx(styles.thumb, large && !banner && styles.thumbContain);
  const uploadIconSize = banner ? 26 : large ? 28 : 18;
  const wrapClass = cx(styles.wrap, banner && styles.wrapBanner, className);

  const hasImage = typeof previewUrl === 'string' && previewUrl.trim().length > 0;
  const useGalleryPanel =
    galleryItems.length > 0 || Boolean(onGallerySelect) || Boolean(onGalleryUpload);

  const applyPastedFile = useCallback(
    (file: File) => {
      if (disabled) return;
      if (inputRef.current && assignFilesToInput(inputRef.current, [file])) {
        setMenuOpen(false);
        setZoomOpen(false);
        return;
      }
      onGalleryUpload?.(file);
      setMenuOpen(false);
      setZoomOpen(false);
    },
    [disabled, onGalleryUpload],
  );

  const { wrapProps } = useClipboardImagePaste({
    disabled: Boolean(disabled),
    accept,
    extraActive: menuOpen || zoomOpen,
    onFile: applyPastedFile,
  });

  const handleFileChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e);
    const file = e.currentTarget.files?.[0];
    if (file) onGalleryUpload?.(file);
    setMenuOpen(false);
    setZoomOpen(false);
  };

  const handleGalleryUpload = (file: File) => {
    onGalleryUpload?.(file);
    setMenuOpen(false);
    setZoomOpen(false);
  };

  const handleGallerySelect = (itemId: string) => {
    onGallerySelect?.(itemId);
    setMenuOpen(false);
    setZoomOpen(false);
  };

  const handleRemove = () => {
    onRemove?.();
    setMenuOpen(false);
    setZoomOpen(false);
  };

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const input = (
    <input
      {...rest}
      ref={inputRef}
      id={inputId}
      type="file"
      className={styles.visuallyHidden}
      accept={accept}
      multiple={multiple}
      name={name}
      disabled={disabled}
      aria-label={chooseLabel}
      onChange={handleFileChange}
    />
  );

  const galleryPanel = useGalleryPanel ? (
    <GalleryImagePickerPanel
      compact
      items={galleryItems}
      selectedId={selectedGalleryId}
      onSelect={handleGallerySelect}
      onUpload={handleGalleryUpload}
      uploadLabel={uploadLabel}
      emptyLabel={emptyGalleryLabel}
      disabled={disabled}
      accept={accept}
    />
  ) : null;

  const legacyMenu = (
    <div className={styles.menu} role="menu" onClick={() => setMenuOpen(false)}>
      <Pressable
        type="button"
        role="menuitem"
        className={styles.menuItem}
        onClick={() => {
          openFilePicker();
          setMenuOpen(false);
        }}
      >
        <span className={styles.menuItemIcon} aria-hidden>
          <IconArrowsExchange size={16} stroke={1.5} />
        </span>
        {changeLabel}
      </Pressable>
      {onRemove ? (
        <>
          <div className={styles.menuDivider} role="separator" />
          <Pressable
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.menuItemDanger)}
            onClick={handleRemove}
          >
            <span className={styles.menuItemIcon} aria-hidden>
              <IconTrash size={16} stroke={1.5} />
            </span>
            {removeLabel}
          </Pressable>
        </>
      ) : null}
    </div>
  );

  const filledMenu = useGalleryPanel ? (
    <div className={styles.menu} role="menu">
      {galleryPanel}
      {onRemove ? (
        <>
          <div className={styles.menuDivider} role="separator" />
          <Pressable
            type="button"
            role="menuitem"
            className={cx(styles.menuItem, styles.menuItemDanger)}
            onClick={handleRemove}
          >
            <span className={styles.menuItemIcon} aria-hidden>
              <IconTrash size={16} stroke={1.5} />
            </span>
            {removeLabel}
          </Pressable>
        </>
      ) : null}
    </div>
  ) : (
    legacyMenu
  );

  const editPopover = (
    <Popover
      opened={menuOpen}
      onChange={setMenuOpen}
      portal
      align="end"
      portalEstimatedHeight={onRemove ? (useGalleryPanel ? 280 : 120) : useGalleryPanel ? 240 : 72}
      portalMinWidth={useGalleryPanel ? 280 : 200}
      portalZIndex="var(--wp-z-popover-over-modal)"
      target={
        <Pressable
          type="button"
          className={cx(styles.editFab, (large || banner) && styles.editFabLg, banner && styles.editFabBanner)}
          disabled={disabled}
          aria-label={changeLabel}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <IconArrowsExchange size={large ? 14 : 12} stroke={1.7} aria-hidden />
        </Pressable>
      }
    >
      {filledMenu}
    </Popover>
  );

  const zoomPortal = hasImage ? (
    <ImageLightbox
      open={zoomOpen}
      onClose={() => setZoomOpen(false)}
      src={previewUrl!}
      label={previewLabel}
      actions={
        <>
          {useGalleryPanel ? (
            <Pressable
              type="button"
              className={imageLightboxActionClass}
              disabled={disabled}
              onClick={() => {
                setZoomOpen(false);
                setMenuOpen(true);
              }}
            >
              {changeLabel}
            </Pressable>
          ) : (
            <Pressable
              type="button"
              className={imageLightboxActionClass}
              disabled={disabled}
              onClick={() => {
                setZoomOpen(false);
                openFilePicker();
              }}
            >
              {changeLabel}
            </Pressable>
          )}
          {onRemove ? (
            <Pressable
              type="button"
              className={cx(imageLightboxActionClass, imageLightboxActionDangerClass)}
              disabled={disabled}
              onClick={handleRemove}
            >
              {removeLabel}
            </Pressable>
          ) : null}
        </>
      }
    />
  ) : null;

  if (!hasImage) {
    if (useGalleryPanel) {
      return (
        <div className={wrapClass} {...wrapProps}>
          {input}
          <Popover
            opened={menuOpen}
            onChange={setMenuOpen}
            portal
            align="start"
            portalEstimatedHeight={200}
            portalMinWidth={280}
            portalZIndex="var(--wp-z-popover-over-modal)"
            target={
              <Pressable
                type="button"
                className={emptyTileClass}
                disabled={disabled}
                aria-label={`${chooseLabel}. Coller une image avec Ctrl+V.`}
                aria-haspopup="dialog"
              >
                <IconUpload size={uploadIconSize} stroke={1.5} aria-hidden />
                {banner ? (
                  <span className={styles.bannerEmptyLabel}>{chooseLabel}</span>
                ) : null}
              </Pressable>
            }
          >
            {filledMenu}
          </Popover>
        </div>
      );
    }

    return (
      <div className={wrapClass} {...wrapProps}>
        {input}
        <Pressable
          type="button"
          className={emptyTileClass}
          disabled={disabled}
          aria-label={`${chooseLabel}. Coller une image avec Ctrl+V.`}
          onClick={openFilePicker}
        >
          <IconUpload size={uploadIconSize} stroke={1.5} aria-hidden />
          {banner ? <span className={styles.bannerEmptyLabel}>{chooseLabel}</span> : null}
        </Pressable>
      </div>
    );
  }

  return (
    <div className={wrapClass} {...wrapProps}>
      {input}
      <div className={cx(styles.tileShell, banner && styles.tileShellBanner)}>
        <Pressable
          type="button"
          className={tileClass}
          disabled={disabled}
          aria-label={`${previewLabel}. Coller une image avec Ctrl+V.`}
          onClick={() => {
            if (disabled) return;
            setZoomOpen(true);
          }}
        >
          <img src={previewUrl!} alt="" className={thumbClass} />
          <span className={styles.hoverOverlay} aria-hidden>
            <IconZoomIn size={banner || large ? 22 : 16} stroke={1.5} />
          </span>
        </Pressable>
        {editPopover}
      </div>
      {zoomPortal}
    </div>
  );
}
